/**
 * Utility functions for handling WebView event communication between web and React Native
 */

import { WEB_VIEW_APP_EVENTS, IdleTimerStage } from "./webApputils/analyticEvents";

/**
 * Type definition for WebView event data
 */
export interface WebViewEventData {
  type: string;
  payload?: any;
}

type EventHandler = (payload?: any) => void;
type EventHandlers = { [key: string]: EventHandler[] };

// Store for event handlers
const eventHandlers: EventHandlers = {};

/**
 * Initialize the WebView event listener
 * This should be called once when your app starts
 */
export const initWebViewEventListener = (): void => {
  if (typeof window === "undefined") return;

  window.addEventListener("message", (event: MessageEvent) => {
    try {
      // Handle both string and object messages
      const data: WebViewEventData =
        typeof event.data === "string" ? JSON.parse(event.data) : event.data;

      if (data && typeof data === "object" && "type" in data) {
        const handlers = eventHandlers[data.type] || [];
        handlers.forEach((handler) => handler(data.payload));
      }
    } catch (error) {
      console.error("Error handling WebView message:", error, "Event data:", event.data);
    }
  });
};

/**
 * Subscribe to events from React Native
 * @param type - The type of event to listen for
 * @param handler - The function to call when the event is received
 * @returns A function to unsubscribe from the event
 */
export const subscribeToWebViewEvent = (type: string, handler: EventHandler): (() => void) => {
  if (!eventHandlers[type]) {
    eventHandlers[type] = [];
  }
  eventHandlers[type].push(handler);

  // Return unsubscribe function
  return () => {
    eventHandlers[type] = eventHandlers[type].filter((h) => h !== handler);
  };
};

/**
 * Sends an event from web to React Native WebView
 * @param type - The type of event to send
 * @param payload - Optional data to send with the event
 */
export const sendWebViewEvent = (type: string, payload?: any): void => {
  if (typeof window !== "undefined" && window.ReactNativeWebView) {
    const eventData: WebViewEventData = {
      type,
      payload,
    };
    window.ReactNativeWebView.postMessage(JSON.stringify(eventData));
  }
};

/**
 * Creates a click handler that sends an event to React Native
 * @param type - The type of event to send on click
 * @param payload - Optional data to send with the event
 * @returns A click event handler function
 */
export const createWebViewClickHandler =
  (type: string, payload?: any) => (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    sendWebViewEvent(type, payload);
  };

// Add TypeScript declaration for ReactNativeWebView
declare global {
  interface Window {
    ReactNativeWebView?: {
      postMessage: (message: string) => void;
    };
    isApp?: boolean;
  }
}

// Idle Timer Notification Functions

interface IdleTimerStartPayload {
  stage: IdleTimerStage;
  redirectUrl: string;
}

interface IdleTimerCancelPayload {
  stage: IdleTimerStage;
}

/**
 * Starts the idle timer for a given stage.
 * Call this when the user exits/abandons a form.
 * @param stage - The form stage (lead_form, book_form, payment)
 * @param redirectUrl - The full URL to redirect to when notification is tapped
 */
export function startIdleTimer(stage: IdleTimerStage, redirectUrl: string) {
  const payload: IdleTimerStartPayload = { stage, redirectUrl };
  sendWebViewEvent(WEB_VIEW_APP_EVENTS.IDLE_TIMER_START, payload);
}

/**
 * Cancels the idle timer for a given stage.
 * Call this when the user successfully completes a form.
 */
export function cancelIdleTimer(stage: IdleTimerStage) {
  const payload: IdleTimerCancelPayload = { stage };
  sendWebViewEvent(WEB_VIEW_APP_EVENTS.IDLE_TIMER_CANCEL, payload);
}
